import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI, resolveSwagImage, SWAG_IMAGE_PLACEHOLDER } from '../config/apiService';

// Swag Items — shared by the SIP and annual programs. Add/edit an item:
//   selection group (group of same item, different colors) -> point value,
//   name / order-form description / item number, the sizes it comes in, and a
//   1000x1000 image (uploaded to blob). All saves are additive.
const blank = {
    swagItemID: null,
    selectionGroupID: '',      // '' | id | 'new'
    newGroupName: '',
    groupPointGroupID: '',     // '' | id | 'new'  (for a new selection group)
    newPointValue: '',
    newPointDesc: '',
    swagItem: '',
    subSelectionDescription: '',
    orderFormDescription: '',
    itemNumber: '',
    sizeIds: [],
    imageName: '',
    discontinued: false,
};

function SwagItemsManager() {
    const [lookups, setLookups] = useState({ pointGroups: [], selectionGroups: [], sizes: [] });
    const [items, setItems] = useState([]);
    const [f, setF] = useState(blank);
    const [showDiscontinued, setShowDiscontinued] = useState(false);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [msg, setMsg] = useState('');
    const [msgKind, setMsgKind] = useState('info');
    const flash = (t, k = 'info') => { setMsg(t); setMsgKind(k); };
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

    const loadAll = async () => {
        try {
            const [lk, it] = await Promise.all([adminAPI.getSwagLookups(), adminAPI.getSwagItems()]);
            setLookups(lk.data || { pointGroups: [], selectionGroups: [], sizes: [] });
            setItems(it.data || []);
        } catch (e) { flash(`Load failed: ${e?.response?.data || e?.message}`, 'error'); }
    };
    useEffect(() => { loadAll(); }, []);

    const groupPoints = useMemo(() => {
        if (f.selectionGroupID === 'new' || f.selectionGroupID === '') return null;
        const g = lookups.selectionGroups.find((x) => x.selectionGroupID === Number(f.selectionGroupID));
        return g ? g.pointGroup : null;
    }, [f.selectionGroupID, lookups.selectionGroups]);

    const editItem = (it) => {
        setF({
            swagItemID: it.swagItemID,
            selectionGroupID: it.selectionGroupID,
            newGroupName: '', groupPointGroupID: '', newPointValue: '', newPointDesc: '',
            swagItem: it.swagItem || '',
            subSelectionDescription: it.subSelectionDescription || '',
            orderFormDescription: it.orderFormDescription || '',
            itemNumber: it.itemNumber || '',
            sizeIds: (it.sizeIds ? String(it.sizeIds).split(',').map(Number) : []),
            imageName: it.imageName || '',
            discontinued: !!it.discontinued,
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const toggleSize = (sizeID) => set('sizeIds',
        f.sizeIds.includes(sizeID) ? f.sizeIds.filter((s) => s !== sizeID) : [...f.sizeIds, sizeID]);

    const onImage = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true); setMsg('');
        try {
            const res = await adminAPI.uploadSwagImage(file);
            if (!res.ok) { const t = await res.text(); throw new Error(t || 'upload failed'); }
            const data = await res.json();
            set('imageName', data.imageName);
            flash('Image uploaded (resized to 1000×1000).', 'success');
        } catch (err) { flash(`Image upload failed: ${err.message}`, 'error'); }
        finally { setUploading(false); }
    };

    const save = async () => {
        if (!f.swagItem.trim()) return flash('Item name is required.', 'error');
        if (!f.orderFormDescription.trim()) return flash('Order form description is required.', 'error');
        setBusy(true); setMsg('');
        try {
            // Resolve the selection group (create point group / group if new).
            let selectionGroupID = f.selectionGroupID;
            if (selectionGroupID === 'new') {
                if (!f.newGroupName.trim()) throw new Error('New selection group needs a name.');
                let pgId = f.groupPointGroupID;
                if (pgId === 'new') {
                    if (f.newPointValue === '') throw new Error('New point group needs a point value.');
                    const pg = await adminAPI.savePointGroup({ PointGroup: Number(f.newPointValue), Description: f.newPointDesc || `${f.newPointValue} points` });
                    pgId = pg.data.pointGroupID;
                } else if (!pgId) throw new Error('Pick a point group for the new selection group.');
                const sg = await adminAPI.saveSelectionGroup({ SelectionGroup: f.newGroupName.trim(), PointGroupID: Number(pgId) });
                selectionGroupID = sg.data.selectionGroupID;
            } else if (!selectionGroupID) throw new Error('Pick a selection group.');

            const res = await adminAPI.saveSwagItem({
                SwagItemID: f.swagItemID,
                SelectionGroupID: Number(selectionGroupID),
                SwagItem: f.swagItem.trim(),
                SubSelectionDescription: f.subSelectionDescription.trim() || null,
                OrderFormDescription: f.orderFormDescription.trim(),
                ImageName: f.imageName || null,
                ItemNumber: f.itemNumber.trim() || null,
                Discontinued: f.discontinued,
                SizeIds: f.sizeIds,
            });
            flash(`Saved (item #${res.data.swagItemID}).`, 'success');
            setF(blank);
            await loadAll();
        } catch (err) { flash(`Save failed: ${err?.response?.data || err.message}`, 'error'); }
        finally { setBusy(false); }
    };

    const visibleItems = items.filter((it) => showDiscontinued || !it.discontinued);

    return (
        <div style={styles.wrap}>
            <div style={styles.eyebrow}>Settings</div>
            <h2 style={styles.h2}>Swag Items</h2>
            <p style={styles.muted}>Shared by both programs. Each item belongs to a selection group (which sets its point value) and defines its sizes.</p>

            {msg && <Banner kind={msgKind}>{msg}</Banner>}

            {/* ---------- form ---------- */}
            <div style={styles.card}>
                <div style={styles.formTitle}>{f.swagItemID ? `Edit item #${f.swagItemID}` : 'Add a swag item'}</div>

                <div style={styles.grid2}>
                    <Field label="Selection group">
                        <select value={f.selectionGroupID} onChange={(e) => set('selectionGroupID', e.target.value === 'new' ? 'new' : Number(e.target.value) || '')} style={styles.input}>
                            <option value="">— pick —</option>
                            {lookups.selectionGroups.map((g) => (
                                <option key={g.selectionGroupID} value={g.selectionGroupID}>{g.selectionGroup} · {g.pointGroup} pts</option>
                            ))}
                            <option value="new">+ New selection group…</option>
                        </select>
                        {groupPoints != null && <div style={styles.hint}>Point value: <strong>{groupPoints}</strong></div>}
                    </Field>

                    {f.selectionGroupID === 'new' && (
                        <Field label="New group name">
                            <input value={f.newGroupName} onChange={(e) => set('newGroupName', e.target.value)} placeholder="e.g. T-Shirts" style={styles.input} />
                        </Field>
                    )}
                </div>

                {f.selectionGroupID === 'new' && (
                    <div style={styles.grid2}>
                        <Field label="Point group for this group">
                            <select value={f.groupPointGroupID} onChange={(e) => set('groupPointGroupID', e.target.value === 'new' ? 'new' : Number(e.target.value) || '')} style={styles.input}>
                                <option value="">— pick —</option>
                                {lookups.pointGroups.map((p) => (
                                    <option key={p.pointGroupID} value={p.pointGroupID}>{p.pointGroup} pts — {p.description}</option>
                                ))}
                                <option value="new">+ New point group…</option>
                            </select>
                        </Field>
                        {f.groupPointGroupID === 'new' && (
                            <div style={styles.grid2}>
                                <Field label="Point value"><input type="number" min="0" value={f.newPointValue} onChange={(e) => set('newPointValue', e.target.value)} style={styles.input} /></Field>
                                <Field label="Description"><input value={f.newPointDesc} onChange={(e) => set('newPointDesc', e.target.value)} placeholder="optional" style={styles.input} /></Field>
                            </div>
                        )}
                    </div>
                )}

                <div style={styles.grid2}>
                    <Field label="Item name"><input value={f.swagItem} onChange={(e) => set('swagItem', e.target.value)} placeholder="e.g. T-Shirt Black" style={styles.input} /></Field>
                    <Field label="Color / sub-selection"><input value={f.subSelectionDescription} onChange={(e) => set('subSelectionDescription', e.target.value)} placeholder="e.g. Black" style={styles.input} /></Field>
                </div>
                <div style={styles.grid2}>
                    <Field label="Order-form description"><input value={f.orderFormDescription} onChange={(e) => set('orderFormDescription', e.target.value)} style={styles.input} /></Field>
                    <Field label="Item number"><input value={f.itemNumber} onChange={(e) => set('itemNumber', e.target.value)} placeholder="optional" style={styles.input} /></Field>
                </div>

                <Field label="Sizes">
                    <div style={styles.sizeWrap}>
                        {lookups.sizes.map((s) => (
                            <label key={s.sizeID} style={{ ...styles.sizeChip, ...(f.sizeIds.includes(s.sizeID) ? styles.sizeChipOn : {}) }}>
                                <input type="checkbox" checked={f.sizeIds.includes(s.sizeID)} onChange={() => toggleSize(s.sizeID)} style={{ marginRight: 5 }} />
                                {s.size}
                            </label>
                        ))}
                    </div>
                </Field>

                <div style={styles.grid2}>
                    <Field label="Image (resized to 1000×1000)">
                        <input type="file" accept="image/*" onChange={onImage} disabled={uploading} style={{ fontSize: 13 }} />
                        {uploading && <div style={styles.hint}>Uploading…</div>}
                    </Field>
                    <div>
                        <img src={resolveSwagImage(f.imageName)} alt="preview" style={styles.preview} onError={(e) => { e.currentTarget.src = SWAG_IMAGE_PLACEHOLDER; }} />
                    </div>
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#374151', marginTop: 10 }}>
                    <input type="checkbox" checked={f.discontinued} onChange={(e) => set('discontinued', e.target.checked)} /> Discontinued (hidden from ordering)
                </label>

                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                    <button onClick={save} disabled={busy || uploading} style={btn('#2563eb', busy || uploading)}>{busy ? 'Saving…' : (f.swagItemID ? 'Save changes' : 'Add item')}</button>
                    {f.swagItemID && <button onClick={() => setF(blank)} disabled={busy} style={btn('#6b7280', busy)}>Cancel edit</button>}
                </div>
            </div>

            {/* ---------- list ---------- */}
            <div style={{ ...styles.card, marginTop: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={styles.formTitle}>Existing items ({visibleItems.length})</div>
                    <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input type="checkbox" checked={showDiscontinued} onChange={(e) => setShowDiscontinued(e.target.checked)} /> show discontinued
                    </label>
                </div>
                <table style={styles.table}>
                    <thead><tr>
                        <th style={styles.th}></th><th style={styles.th}>Item</th><th style={styles.th}>Group</th>
                        <th style={{ ...styles.th, textAlign: 'right' }}>Pts</th><th style={styles.th}>Sizes</th><th style={styles.th}></th>
                    </tr></thead>
                    <tbody>
                        {visibleItems.map((it) => (
                            <tr key={it.swagItemID} style={{ opacity: it.discontinued ? 0.5 : 1 }}>
                                <td style={styles.td}>
                                    <img src={resolveSwagImage(it.imageName)} alt="" style={styles.thumb} onError={(e) => { e.currentTarget.src = SWAG_IMAGE_PLACEHOLDER; }} />
                                </td>
                                <td style={styles.td}>{it.swagItem}{it.discontinued && <span style={styles.disc}> discontinued</span>}</td>
                                <td style={styles.td}>{it.selectionGroup}</td>
                                <td style={{ ...styles.td, textAlign: 'right' }}>{it.pointGroup}</td>
                                <td style={{ ...styles.td, color: '#6b7280' }}>{sizeNames(it.sizeIds, lookups.sizes)}</td>
                                <td style={styles.td}><button onClick={() => editItem(it)} style={btn('#6b7280', false)}>Edit</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const sizeNames = (csv, sizes) => {
    if (!csv) return '—';
    const map = new Map(sizes.map((s) => [s.sizeID, s.size]));
    return String(csv).split(',').map((id) => map.get(Number(id)) || id).join(', ');
};

function Field({ label, children }) {
    return <div style={{ marginTop: 10 }}><div style={styles.label}>{label}</div>{children}</div>;
}
function Banner({ kind, children }) {
    const color = kind === 'error' ? '#dc2626' : kind === 'success' ? '#15803d' : '#374151';
    const bg = kind === 'error' ? '#fef2f2' : kind === 'success' ? '#f0fdf4' : '#f3f4f6';
    return <div style={{ margin: '12px 0', padding: '8px 12px', borderRadius: 4, background: bg, color, fontSize: 13 }}>{children}</div>;
}
const btn = (bg, disabled) => ({ background: disabled ? '#cbd5e1' : bg, color: 'white', border: 'none', borderRadius: 6, padding: '7px 14px', fontSize: 13, fontWeight: 700, cursor: disabled ? 'default' : 'pointer' });

const styles = {
    wrap: { padding: 24, maxWidth: 900 },
    eyebrow: { fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: '#9ca3af', fontWeight: 700 },
    h2: { margin: '2px 0 4px', fontSize: 22, fontWeight: 800, color: '#111827' },
    muted: { color: '#6b7280', fontSize: 13, lineHeight: 1.5, margin: '4px 0 12px' },
    card: { background: 'white', padding: 16, borderRadius: 10, border: '1px solid #e5e7eb' },
    formTitle: { fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 6 },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
    label: { fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.3 },
    input: { width: '100%', padding: '7px 9px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' },
    hint: { fontSize: 12, color: '#6b7280', marginTop: 3 },
    sizeWrap: { display: 'flex', gap: 6, flexWrap: 'wrap' },
    sizeChip: { display: 'inline-flex', alignItems: 'center', padding: '5px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, cursor: 'pointer', background: 'white' },
    sizeChipOn: { background: '#dcfce7', borderColor: '#86efac', color: '#166534', fontWeight: 700 },
    preview: { width: 100, height: 100, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb' },
    previewEmpty: { width: 100, height: 100, borderRadius: 8, border: '1px dashed #d1d5db', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 12 },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: 8 },
    th: { textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4, color: '#6b7280', borderBottom: '2px solid #e5e7eb', padding: '6px 8px' },
    td: { fontSize: 13, color: '#111827', borderBottom: '1px solid #f3f4f6', padding: '6px 8px', verticalAlign: 'middle' },
    thumb: { width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' },
    thumbEmpty: { width: 40, height: 40, borderRadius: 6, background: '#f3f4f6', border: '1px solid #e5e7eb' },
    disc: { fontSize: 11, color: '#dc2626', fontWeight: 700 },
};

export default SwagItemsManager;
